import { Component, Story, Knobs } from './DocTypes';

const PlaygroundStoryKnobs: Knobs = {
  name: { default: '', editor: 'textbox' },
  value: { default: '2', editor: 'dropdown', editorOptions: ['1', '2', '3'] },
  disabled: { default: false, editor: 'checkbox' },
  dropDownStyle: { default: '', editor: 'textbox' },
  colourScheme: {
    editor: 'dropdown',
    editorOptions: ['', 'light', 'dark', 'default'],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['', 'mobile', 'desktop'],
    default: '',
  },
};

const PlaygroundStoryKnobs_React: Knobs = {
  ...PlaygroundStoryKnobs,
  dropDownStyle: {
    ...PlaygroundStoryKnobs.dropDownStyle,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A dropdown where you can change all the attributes',
  coreComponent_v3: {
    html: `<tag3-dropdown{{...knobs}}></tag3-dropdown>`,
    js: `var dropdown = document.querySelector("tag3-dropdown");
dropdown.data = [
  { label: "Red", value: 1 },
  { label: "Blue", value: 2 },
  { label: "Green", value: 3 },
];`,
    knobs: PlaygroundStoryKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Tag3DropDown
{{...knobs}} 
  data={[
    { label: "Red", value: 1 },
    { label: "Blue", value: 2 },
    { label: "Green", value: 3 },
  ]}
/>`,
    knobs: PlaygroundStoryKnobs_React,
  },
};

const StringArrayStory: Story = {
  title: 'String array',
  description: 'A dropdown bound to a simple string array',
  coreComponent_v3: {
    html: `<tag3-dropdown></tag3-dropdown>`,
    js: `var dropdown = document.querySelector("tag3-dropdown");
dropdown.data = ["Red", "Blue", "Green"];`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3DropDown data={["Red", "Blue", "Green"]} />`,
  },
};

const CustomDataSourceStory: Story = {
  title: 'Custom data source',
  description:
    'A dropdown where the label and value are bound specific properties in the data source.',
  coreComponent_v3: {
    html: `<tag3-dropdown text-field="color" value-field="colorId"></tag3-dropdown>`,
    js: `var dropdown = document.querySelector("tag3-dropdown");
dropdown.data = [
  { color: "Red", colorId: 1 },
  { color: "Blue", colorId: 2 },
  { color: "Green", colorId: 3 },
];`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3DropDown
  textField="color" 
  valueField="colorId"
  data={[
    { color: "Red", colorId: 1 },
    { color: "Blue", colorId: 2 },
    { color: "Green", colorId: 3 },
  ]}
/>`,
  },
};

const ChildElementsStory: Story = {
  title: 'Child elements',
  description: 'A dropdown with options defined in HTML',
  coreComponent_v3: {
    html: `<tag3-dropdown>
  <tag3-dropdown-option value="1" label="Red"></tag3-dropdown-option>
  <tag3-dropdown-option value="2" label="Blue"></tag3-dropdown-option>
  <tag3-dropdown-option value="3" label="Green"></tag3-dropdown-option>
</tag3-dropdown>`,
  },
};

const ControlledValueStory: Story = {
  title: 'Controlled value',
  description: 'A dropdown where the value is controlled by state',
  reactWrapper_v3: {
    parentJSX: `Example = () => {
  const [value, setValue] = React.useState(2);
  return (
    <Tag3DropDown
      value={value}
      onDropDownValueChange={e => setValue(e.detail.value)}
      data={[
        { label: "Red", value: 1 },
        { label: "Blue", value: 2 },
        { label: "Green", value: 3 },
      ]}
    />
  )
}
`,
    jsx: '<Example />',
  },
};

export const TagDropDownDocs: Component = {
  type: 'component',
  name: 'dropdown',
  description: 'Renders a dropdown',
  mobileFriendly: false,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'dropDownId',
      description: 'A unique id for the dropdown',
      type: 'string',
    },
    {
      name: 'name',
      description: 'A unique name for the dropdown',
      type: 'string',
    },
    {
      name: 'value',
      description: 'The selected value',
      type: 'any',
    },
    {
      name: 'disabled',
      description: 'Whether the dropdown is disabled or not',
      type: 'boolean',
    },
    {
      name: 'textField',
      description: 'The field in the data object array to use as the option display text',
      type: 'string',
    },
    {
      name: 'valueField',
      description: 'The field in the data object array to use as the option value',
      type: 'string',
    },
    {
      name: 'dropDownStyle',
      description: 'JSX style object in JSON to apply to the checkbox',
      type: 'string',
    },
    {
      name: 'colourScheme',
      description:
        'Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'string',
    },
    {
      name: 'device',
      description: 'set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'dropDownValueChange',
      description: 'Raised when the selected value changes',
      parameters: [{ name: 'value', description: 'The value', type: 'any' }],
    },
    {
      name: 'dropDownBlur',
      description: 'Event raised when the component loses focus',
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'component name',
        },
      ],
    },
    {
      name: 'dropDownFocus',
      description: 'Event raised when the component gains focus',
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'component name',
        },
      ],
    },
  ],
  methods: [
    {
      name: 'focusDropDown',
      description: 'Sets the focus to the dropdown',
      parameters: [],
      returnType: 'void',
    },
  ],
  stories: [
    PlaygroundStory,
    StringArrayStory,
    CustomDataSourceStory,
    ChildElementsStory,
    ControlledValueStory,
  ],
};
